import React, { useEffect, useState } from "react";
import {
    Chip,
    CircularProgress,
    Link,
    Paper,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { formatDateTime } from "../../utils/DateTimeUtils";
import {
    CustomTableRowTR,
    StyledTable,
    TableData,
    TableHeader,
    TableTopHeader,
    TableWrapper,
    Title,
} from "../../styles/commonStyles";
import { RefundServiceV2 } from "../../services/RefundServiceV2";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";

const refundServiceV2 = new RefundServiceV2();

const getStatusChipStyles = (status?: string) => {
    switch ((status || "").toUpperCase()) {
        case "SUCCESS":
        case "PROCESSED":
            return { color: "#047857", background: "rgba(16, 185, 129, 0.16)" };
        case "PENDING":
            return { color: "#B45309", background: "rgba(245, 158, 11, 0.18)" };
        case "FAILED":
            return { color: "#B91C1C", background: "rgba(239, 68, 68, 0.16)" };
        default:
            return { color: "#6B7280", background: "rgba(107, 114, 128, 0.16)" };
    }
};

interface ServiceRefundsTabProps {
    referenceId: number;
    referenceModel: string;
}

const ServiceRefundsTab = ({ referenceId, referenceModel }: ServiceRefundsTabProps) => {
    const [refunds, setRefunds] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!referenceId || !referenceModel) return;
        setLoading(true);
        refundServiceV2
            .getRefundsByReferenceId(referenceId, referenceModel)
            .then((data) => {
                setRefunds(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Failed to load refunds",
                    type: CustomSnackbarTypes.ERROR,
                });
            })
            .finally(() => setLoading(false));
    }, [referenceId, referenceModel]);

    return (
        <TableWrapper>
            <TableTopHeader>
                <Title>Refunds</Title>
            </TableTopHeader>
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 2,
                    overflowX: "auto",
                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                <StyledTable size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                            <TableHeader>ID</TableHeader>
                            <TableHeader>Status</TableHeader>
                            <TableHeader>Amount</TableHeader>
                            <TableHeader>Type</TableHeader>
                            <TableHeader>Refunded By</TableHeader>
                            <TableHeader>Refund Processed At</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : refunds.length > 0 ? (
                            refunds.map((refund) => (
                                <CustomTableRowTR key={refund.id}>
                                    <TableData>{refund.id ?? "-"}</TableData>
                                    <TableData>
                                        <Chip
                                            label={refund.status || "-"}
                                            size="small"
                                            sx={{
                                                ...getStatusChipStyles(refund.status),
                                                fontWeight: 600,
                                            }}
                                        />
                                    </TableData>
                                    <TableData>{refund.amount ?? "-"}</TableData>
                                    <TableData>{refund.refundType ?? "-"}</TableData>
                                    <TableData>
                                        {refund.refundedBy ? (
                                            <Link
                                                href={`/#/AdminUsers/${refund.refundedBy}/show`}
                                                underline="hover"
                                            >
                                                {refund.refundedBy}
                                            </Link>
                                        ) : (
                                            "-"
                                        )}
                                    </TableData>
                                    <TableData>
                                        <span style={{ whiteSpace: "nowrap" }}>
                                            {refund.refundProcessedAt
                                                ? formatDateTime(refund.refundProcessedAt)
                                                : "-"}
                                        </span>
                                    </TableData>
                                </CustomTableRowTR>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    No refunds found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        </TableWrapper>
    );
};

export default ServiceRefundsTab;
